/**
 * COURSE LINKS — `/learn/<course_code>` (job #124).
 *
 * Tom, 2026-09-23 sweep: deep links over pickers; 2026-09-29: landing pages
 * need a link straight into each course, India's nine eng_for_* first. One
 * short, human address per course: saysomethingin.app/learn/eng_for_hin.
 *
 * The app already knows how to open a named course: `/?course=<code>` is read
 * at boot by the locale inference (utils/deepLinkLocale), the bundle warm-up,
 * the first-load picker (platform/pickerFirst, which stands aside for it) and
 * App's course resolution, which opens it on preview for anyone and carries
 * it into the paywall's market pricing. So this link is a SPELLING of that
 * one, rewritten in main.js before anything reads the address. Nothing below
 * the rewrite has to learn a second door.
 *
 * Unknown codes: a code that is not even shaped like one goes straight to
 * the course picker (`?openCourses=1`). A well-shaped code the catalogue does
 * not hold is passed on, and App opens the picker once the catalogue says so
 * (requestCoursePicker below).
 */
import { ref } from 'vue'

/** Shaped like a catalogue code: `eng_for_hin`, `cym_n_for_eng`, `community_...`. */
const COURSE_CODE = /^[a-z][a-z0-9_]{2,63}$/

const LEARN_PATH = /^\/learn(?:\/([^/]*))?\/?$/

/**
 * The address a `/learn/...` path stands for, or null when the path is not
 * one. Other query keys (utm tags, referrers) ride along untouched.
 */
export function learnLinkTarget(pathname: string, search: string): string | null {
  const m = LEARN_PATH.exec(pathname || '')
  if (!m) return null
  let code = ''
  try {
    code = decodeURIComponent(m[1] || '').trim().toLowerCase()
  } catch {
    code = ''
  }
  const params = new URLSearchParams(search || '')
  params.delete('course')
  params.delete('openCourses')
  if (COURSE_CODE.test(code)) params.set('course', code)
  else params.set('openCourses', '1')
  return `/?${params.toString()}`
}

/**
 * Rewrite the current address in place when it is a course link. Called once
 * from main.js, before the locale inference, the router and the app read it.
 */
export function rewriteLearnLink(loc: Location = window.location, hist: History = window.history): boolean {
  const target = learnLinkTarget(loc.pathname, loc.search)
  if (!target) return false
  hist.replaceState(hist.state, '', target + (loc.hash || ''))
  return true
}

/**
 * A `/learn/...` link followed INSIDE the running app (job #181, lane review
 * #159). The course a link names is read ONCE, at boot: App's course
 * resolution, the locale inference and the bundle warm-up all read the address
 * as the app starts and never again. So a soft route change to `/?course=…`
 * while another course is mounted changed the address and nothing else: the
 * old course played on under the new link. The only honest way to follow a
 * course link from inside the app is to leave and come back — a hard
 * navigation, exactly as the `/reset` alias does — so the boot handling runs.
 *
 * The navigator is swappable so the router's record can be driven in a test.
 */
export const learnLinkNavigator = {
  go(url: string): void {
    // Under the app's own base: '/' in production, '/play/<who>/' on a PLAY
    // copy, so the hard navigation lands on the same copy that followed it.
    const base = (import.meta.env?.BASE_URL || '/').replace(/\/$/, '')
    window.location.assign(base + url)
  },
}

/** Follow a course link from inside the app: hard-navigate to its target. */
export function followLearnLinkInApp(pathname: string, search: string, hash = ''): string | null {
  const target = learnLinkTarget(pathname, search)
  if (!target) return null
  learnLinkNavigator.go(target + (hash || ''))
  return target
}

/**
 * Where a link naming a course the catalogue does NOT hold lands (red-team
 * #133 finding 11: `/learn/eng_for_xyzzy` opened Afrikaans in English with the
 * picker floating over it, and remembered Afrikaans for the next visit).
 *
 * A stranger — nobody saved a course on this device or account — goes to the
 * first-load picker page, the same page a stranger with no link at all gets,
 * and no course is assigned behind their back. Anyone with a saved course
 * keeps it, with the picker opened over it: their course is still theirs, the
 * typo is not a reason to lose it.
 */
export type UnknownCourseLanding = 'first-picker' | 'picker-over-saved-course'

export function unknownCourseLanding(savedCourseCode: string | null | undefined): UnknownCourseLanding {
  return savedCourseCode ? 'picker-over-saved-course' : 'first-picker'
}

/**
 * Set by App when a linked course is not in the catalogue; the player opens
 * the course picker on it (PlayerContainer watches this). A ref rather than a
 * window event because the catalogue can answer before the player has mounted.
 */
export const coursePickerRequested = ref(false)

export function requestCoursePicker(): void {
  coursePickerRequested.value = true
}

/**
 * Should a signed-in learner's saved course be moved to the linked one? Only
 * when a link actually named a course that opened, and the saved one differs.
 */
export function linkedCourseToSave(
  linkedCourse: string | null | undefined,
  activeCourse: string | null | undefined,
  savedCourse: string | null | undefined,
): string | null {
  if (!linkedCourse || linkedCourse !== activeCourse) return null
  return savedCourse === linkedCourse ? null : linkedCourse
}

/**
 * Pages whose `?course=` names a course to LOOK AT, not to play: the Brain
 * (`/brain?course=`), the Phrase redesign (`/phrases?course=`, job #249), and the intel, admin and org views that filter by
 * course. Opening one by its address (a reload, a pasted link) must not move
 * the learner's player onto that course, nor save it as their course on the
 * account. Before this, reloading Romanian's Brain switched a French learner's
 * player and saved course to Romanian (job #211).
 */
const VIEWER_PATH = /^\/(brain|phrases|intel|admin|org)(\/|$)/

/** The course a page address asks the PLAYER to open, or null when it asks none. */
export function playCourseFromAddress(pathname: string, search: string): string | null {
  if (VIEWER_PATH.test(pathname || '')) return null
  try {
    return new URLSearchParams(search || '').get('course')
  } catch {
    return null
  }
}
